import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type StylizeMode = "find-edges" | "emboss";

export interface StylizeParams {
  mode: StylizeMode;
  angle: number;
  height: number;
  amount: number;
}

const SOBEL_X = [
  [-1, 0, 1],
  [-2, 0, 2],
  [-1, 0, 1],
];

const SOBEL_Y = [
  [-1, -2, -1],
  [0, 0, 0],
  [1, 2, 1],
];

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function sobelGradientAt(
  src: Uint8ClampedArray,
  w: number,
  h: number,
  x: number,
  y: number,
  channel: number,
): { gx: number; gy: number } {
  let gx = 0;
  let gy = 0;
  for (let ky = 0; ky < 3; ky++) {
    let sy = y + ky - 1;
    if (sy < 0) sy = 0;
    else if (sy >= h) sy = h - 1;
    for (let kx = 0; kx < 3; kx++) {
      let sx = x + kx - 1;
      if (sx < 0) sx = 0;
      else if (sx >= w) sx = w - 1;
      const o = (sy * w + sx) * 4 + channel;
      const kvx = SOBEL_X[ky]![kx]!;
      const kvy = SOBEL_Y[ky]![kx]!;
      gx += src[o]! * kvx;
      gy += src[o]! * kvy;
    }
  }
  return { gx, gy };
}

function applyFindEdges(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
): void {
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const y = Math.floor(i / w);
    const x = i % w;
    const o = i * 4;

    const rg = sobelGradientAt(src, w, h, x, y, 0);
    const gg = sobelGradientAt(src, w, h, x, y, 1);
    const bg = sobelGradientAt(src, w, h, x, y, 2);

    const edge =
      Math.sqrt(
        rg.gx * rg.gx +
          rg.gy * rg.gy +
          gg.gx * gg.gx +
          gg.gy * gg.gy +
          bg.gx * bg.gx +
          bg.gy * bg.gy,
      ) / 3;

    const val = 255 - clamp8(Math.round(edge));
    dst[o] = val;
    dst[o + 1] = val;
    dst[o + 2] = val;
    dst[o + 3] = src[o + 3]!;
  }
}

function applyEmboss(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  angle: number,
  height: number,
  amount: number,
): void {
  const rad = (angle * Math.PI) / 180;
  const cosA = Math.cos(rad);
  const sinA = Math.sin(rad);
  const strength = (height * amount) / 10000;

  const n = w * h;
  for (let i = 0; i < n; i++) {
    const y = Math.floor(i / w);
    const x = i % w;
    const o = i * 4;

    const rg = sobelGradientAt(src, w, h, x, y, 0);
    const gg = sobelGradientAt(src, w, h, x, y, 1);
    const bg = sobelGradientAt(src, w, h, x, y, 2);

    const gradR = cosA * rg.gx + sinA * rg.gy;
    const gradG = cosA * gg.gx + sinA * gg.gy;
    const gradB = cosA * bg.gx + sinA * bg.gy;

    dst[o] = clamp8(Math.round(128 + gradR * strength));
    dst[o + 1] = clamp8(Math.round(128 + gradG * strength));
    dst[o + 2] = clamp8(Math.round(128 + gradB * strength));
    dst[o + 3] = src[o + 3]!;
  }
}

export const StylizeSuite: Filter<StylizeParams> = {
  id: "stylize-suite",
  displayName: "Stylize",
  defaultParams: { mode: "find-edges", angle: 135, height: 3, amount: 100 },

  apply(
    input: PixelBuffer,
    params: StylizeParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    switch (params.mode) {
      case "find-edges":
        applyFindEdges(src, dst, w, h);
        break;
      case "emboss":
        applyEmboss(src, dst, w, h, params.angle, params.height, params.amount);
        break;
    }

    return out;
  },
};
